import {
  POLICY_DOC,
  activityIdFromItem,
  approveBlockers,
  challengeBlockers,
  contractField,
  definitionFingerprint,
  fieldCommentTarget,
  fieldIdFromCommentTarget,
  moveBackBlockers,
  paapiChanges,
  policyTitle,
  publishBlockers,
  publishNowBlockers,
  readPolicyDoc,
  recordBlockers,
  respondBlockers,
} from '@worker/policy-builder/client'
import type {
  AnswerValue,
  Citation,
  CodeList,
  ContractVersion,
  DefinitionSnapshot,
  NodeLinks,
  NucleusClaimResponse,
  NucleusJdm,
  PaapiBaseline,
  PaapiChange,
  PolicyActivity,
  PolicyAgent,
  PolicyDocView,
  PolicyRole,
  PolicySource,
  PolicyStage,
  PolicySummary,
  TreeVersion,
  VersionRecord,
} from '@worker/policy-builder/client'
import type { AgentClient } from 'agents/client'
import type {
  CollaborationAgentClient,
  PresencePeer,
  ResolvedComment,
  UsePresenceIdentityReturn,
} from '@rialtic/nuxt-shared'
import {
  POLICY_REGISTRY_KEYS,
  policySummaryQuery,
} from '~/queries/policy-studio/policies'
import { applyTextDiff } from '~/utils/loroText'
import { errorMessage } from '~/utils/policy-studio/errors'
import type {
  DecisionTree,
  NodeMove,
  TreeDirection,
} from '~/utils/policy-studio/collabTree'
import {
  DESIGN_LISTS,
  applyDesignPatch,
  applyTreeEdit,
  clearDirection,
  decisionCitation,
  removeById,
  replaceFieldCitations,
  upsertById,
  writePositions,
} from '~/utils/policy-studio/policyDocWrites'
import type {
  DesignListName,
  DesignPatch,
  EntryList,
} from '~/utils/policy-studio/policyDocWrites'
import { people } from '~/utils/policy-studio/presence'

type MovableContainer = typeof POLICY_DOC.sources | typeof POLICY_DOC.citations

export interface PolicyRoom {
  collab: CollaborationAgentClient
  /** This tab in the room: cursor and selection go out through `update`. */
  presence: UsePresenceIdentityReturn
  /** Everyone else in the room, one entry per person. */
  peers: ComputedRef<PresencePeer[]>
  /** Registry row; `undefined` until it loads. */
  summary: Ref<PolicySummary | undefined>
  /** The registry has no such policy — render a not-found state. */
  notFound: ComputedRef<boolean>
  /** Plain read of the whole document; re-evaluates on every change. */
  view: ComputedRef<PolicyDocView>
  /** Scoping's policy name, else the created title, else the registry's until the room syncs. */
  title: ComputedRef<string>
  /**
   * Content Library's copy of the policy, read when the room opens and again
   * after each stage move; `null` until the first read lands.
   */
  contentLibrary: Readonly<Ref<PaapiBaseline | null>>
  /** Rows publish would write: the draft differs from Content Library's copy. */
  contentLibraryChanges: ComputedRef<PaapiChange[]>
  stage: ComputedRef<PolicyStage>
  /**
   * There is no freezing: anyone may edit any part of an active policy.
   * Integrity comes from versions and decisions, not from a stage lock.
   */
  editable: ComputedRef<boolean>
  /**
   * One edit to the decision tree: `change` gets the current tree and returns
   * the next; only the entries that differ are written, in one commit (one
   * undo step), along with where any `placed` nodes sit. Nodes the edit keeps
   * stay where they are (`heldPlaces`), rather than moving with the layout.
   */
  editTree: (
    change: (tree: DecisionTree) => DecisionTree,
    placed?: { direction: TreeDirection; moves: NodeMove[] },
  ) => void
  /** Where nodes were dragged to, for one layout direction; one undo step. */
  moveNodes: (direction: TreeDirection, moves: NodeMove[]) => void
  /** Forgets every dragged position in one direction, back to the computed layout. */
  tidyLayout: (direction: TreeDirection) => void
  /** The design tree's saved versions and editing sessions, newest first. */
  treeVersions: () => Promise<{ versions: TreeVersion[] }>
  /** The document as it stood at a version; throws when this tab hasn't synced that far. */
  viewAt: (frontiers: TreeVersion['frontiers']) => PolicyDocView
  /** Saves where the tree stands now to its version history. */
  saveTreeVersion: (name: string, description?: string) => Promise<void>
  /** Puts the tree back as it was at a version, as a new edit. */
  restoreTreeVersion: (versionId: string) => Promise<void>
  /** The definition as it stood at a cut version, for "What changed". */
  versionSnapshot: (seq: number) => Promise<DefinitionSnapshot | null>
  /** What a tree node is traced to: the contract rows it implements. */
  linkNode: (nodeId: string, links: NodeLinks) => void
  /** May comment and decline — any stage, unlike the container editors. */
  canDiscuss: ComputedRef<boolean>
  setAnswer: (fieldId: string, value: AnswerValue) => void
  addCitation: (citation: Omit<Citation, 'id' | 'by' | 'at'>) => void
  /** Replaces one field's citations wholesale, leaving every other field's alone. */
  setFieldCitations: (fieldId: string, citations: Citation[]) => void
  markNotAsked: (fieldId: string, reason: string) => void
  pullIn: (fieldId: string) => void
  addSource: (source: PolicySource) => void
  removeSource: (id: string) => void
  /** Writes whole design lists and meta fields, touching only those that differ. */
  setDesign: (patch: DesignPatch) => void
  /** Creates, replaces or clears a code list, bound to tree nodes. */
  setCodeList: (id: string, list: CodeList | null) => void
  setDescription: (next: string) => void
  ask: (
    fieldId: string,
    text: string,
    opts?: { to?: PolicyRole; mentions?: string[] },
  ) => Promise<void>
  /** `basis` records what settles a judgment-call answer: a citation on the field, a written rationale, or both. */
  answer: (
    id: string,
    text: string,
    basis?: { citationId?: string; rationale?: string },
  ) => Promise<void>
  closeQuestion: (id: string) => Promise<void>
  reassign: (role: PolicyRole, owner: string | null) => Promise<void>
  /** Approves this role's review of the current definition. */
  approve: (
    role: PolicyRole,
    opts?: { note?: string; evidenceUrl?: string },
  ) => Promise<void>
  /** Sends the policy back to scoping or design with an explanation. */
  challenge: (
    to: 'scoping' | 'design',
    explanation: string,
    evidenceUrl?: string,
  ) => Promise<void>
  /** Returns the policy to an earlier stage: the one before by default, or
   *  `to` when an earlier team pulls it back. */
  moveBack: (explanation: string, to?: PolicyStage) => Promise<void>
  /** Answers a challenge without changing the definition. */
  respond: (explanation: string) => Promise<void>
  /** Approves the policy record (change type, summary, lines of business). */
  approveRecord: (record: VersionRecord) => Promise<void>
  publishPolicy: () => Promise<void>
  /** Withdraws an open update: back to the version last published (U5). */
  withdraw: (note?: string) => Promise<void>
  /** Links external test results for a policy NURO didn't draft (B1). */
  attestClaims: (evidenceUrl: string) => Promise<void>
  /** Validation's one action: approve this version if still needed, record it, publish. */
  publishNow: (record: VersionRecord, evidenceUrl?: string) => Promise<void>
  /** Opens an update on a published policy: to scoping for a change, or to
   *  design for a defect fix. */
  reopen: (note?: string, to?: 'scoping' | 'design') => Promise<void>
  /** Sends free-text feedback about one node to Nucleus. */
  /** `nodeId` null steers the whole tree (D6). */
  steerNode: (nodeId: string | null, feedback: string) => Promise<void>
  /** Pushes the tree to Nucleus now, instead of waiting for the idle schedule. */
  syncJdm: () => Promise<void>
  /** Pushes the tree and generates Nucleus's deterministic test claims. */
  generateClaims: () => Promise<void>
  judgeClaim: (
    name: string,
    verdict: 'agree' | 'disagree',
    cause?: 'unclear-intent' | 'bad-claim' | 'wrong-logic',
    note?: string,
    question?: string,
  ) => Promise<void>
  /** `null` clears the mark. */
  markUnreachable: (pathKey: string, reason: string | null) => Promise<void>
  /** The stored Nucleus fixture plus the JDM graph it ran against, for the
   *  Claims tab's path detail; `null` before claims are generated. */
  /** The Nucleus JDM design's latest approval stored, `null` before one. */
  approvedJdm: () => Promise<Awaited<ReturnType<PolicyAgent['approvedJdm']>>>
  claimSet: () => Promise<{
    fixture: NucleusClaimResponse['fixture']
    jdm: NucleusJdm
  } | null>
  /** Why each gated move would be refused, in the agent's own words. */
  blockers: {
    approve: (role: PolicyRole) => string[]
    challenge: (input: { explanation: string; evidenceUrl?: string }) => string[]
    moveBack: (explanation: string, to?: PolicyStage) => string[]
    respond: (text: string) => string[]
    record: (record: VersionRecord) => string[]
    publish: () => string[]
    publishNow: (record: VersionRecord, evidenceUrl?: string) => string[]
  }
  /** One page of the audit trail, newest first; `q` narrows it to entries mentioning it. */
  activity: (query?: {
    cursor?: string
    q?: string
  }) => Promise<{ entries: PolicyActivity[]; nextCursor: string | null }>
  comments: ComputedRef<ResolvedComment[]>
  /** Comment on a contract row or tree node (`nodeItemId`), or reply when `parentId` is set. `false` when offline. */
  comment: (
    itemId: string,
    body: string,
    mentions: string[],
    parentId?: string,
  ) => boolean
  resolveThread: (id: string) => boolean
  reopenThread: (id: string) => boolean
  acceptSuggestion: (id: string) => Promise<void>
  /** Accepts a field's machine proposal as-is (C3). */
  confirmProposal: (fieldId: string) => Promise<void>
  declineSuggestion: (id: string, feedback?: string) => Promise<void>
  undoField: (fieldId: string) => Promise<void>
  redoField: (fieldId: string) => Promise<void>
  restoreField: (fieldId: string, versionId: string) => Promise<void>
  fieldHistory: (
    fieldId: string,
    cursor?: string,
  ) => Promise<{ versions: ContractVersion[]; nextCursor: string | null }>
  /** Notes on the policy, a contract row or tree node (`nodeItemId`): any stage, authored by the connection; only the author edits or removes. */
  addNote: (itemId: string, body: string) => Promise<void>
  editNote: (id: string, body: string) => Promise<void>
  removeNote: (id: string) => Promise<void>
  /** Saves a thread's opening comment as a note on what it is on (policy, row or node), then resolves the thread. Throws for a thread on an activity entry. */
  noteFromThread: (root: ResolvedComment) => Promise<void>
  /** Records a contract-row thread's opening comment as a written decision on that row, then resolves the thread; `false` when the policy is not editable or the thread is not on a row. */
  decisionFromThread: (root: ResolvedComment) => boolean
}

/**
 * One policy's collaborative room.
 *
 * Container writers go straight into Loro (every peer sees them live) and are
 * no-ops once the policy is not editable, so the page never sends a write the
 * agent would revert. There is no freezing by stage: `editable` is false only
 * while offline, read-only, dismissed, or published and live. Approvals,
 * challenges, responses, publish and reopen are authoritative and go to the
 * agent as commands, each pinned to the definition it reviewed.
 *
 * The socket opens only once the registry confirms the policy exists: any name
 * creates a Durable Object on first touch, so a mistyped URL must not connect.
 * `policyId` is read once, as the room name is — key the page on it. Pass
 * `{ persistence: false }` for a short-lived extra room (an inline reply), so
 * its stale offline copy can't overwrite the page room's on unmount.
 */
export const usePolicyRoom = (
  policyId: MaybeRefOrGetter<string>,
  { persistence }: { persistence?: false } = {},
): PolicyRoom => {
  const { data: summary, error } = useQuery(() =>
    policySummaryQuery(toValue(policyId)),
  )
  const notFound = computed(() => {
    const failure = error.value
    return Boolean(
      failure &&
        typeof failure === 'object' &&
        'statusCode' in failure &&
        failure.statusCode === 404,
    )
  })

  const peerId = crypto.randomUUID()
  const { user } = useUserSession()

  const collab = useCollaborationAgent({
    agent: 'policy-agent',
    name: toValue(policyId),
    host: useRuntimeConfig().public.POLICY_BUILDER_HOST,
    textContainer: POLICY_DOC.description,
    peerId,
    query: useCollabAuthQuery(),
    autoConnect: false,
    persistence,
  })
  watch(
    () => Boolean(summary.value),
    (exists) => {
      if (exists) collab.connect()
    },
    { immediate: true },
  )

  const doc = collab.document.doc
  const view = computed<PolicyDocView>(() => {
    void collab.document.version.value
    return readPolicyDoc(doc)
  })
  const stage = computed(() => view.value.meta.stage)

  const presence = usePresenceIdentity(collab, () => ({
    id: peerId,
    name: user.value?.name || user.value?.email || 'anonymous',
    picture: user.value?.picture,
    user: user.value?.sub,
  }))
  const peers = computed(() => people(presence.peers.value, user.value?.sub))

  const canWrite = useCanWriteContent()
  const editable = computed(
    () =>
      canWrite.value &&
      collab.status.value === 'open' &&
      stage.value !== 'dismissed' &&
      !(stage.value === 'published' && view.value.meta.publishedAt),
  )

  const commit = (write: () => void): void => {
    if (!editable.value) return
    write()
    doc.commit()
  }
  /** Replaces the entry with `id` in a movable list, or appends it. */
  const upsert = (container: MovableContainer, entry: PolicySource): void =>
    commit(() => upsertById(doc.getMovableList(container), entry))

  const removeEntry = (container: MovableContainer, id: string): void =>
    commit(() => removeById(doc.getMovableList(container), id))

  const agent = collab.connection as unknown as AgentClient<PolicyAgent>
  const queryCache = useQueryCache()
  /** Runs a command, then refreshes every registry read it may have changed. */
  const send = async (
    message: Parameters<PolicyAgent['command']>[0],
  ): Promise<void> => {
    await agent.call('command', [message])
    void queryCache.invalidateQueries({ key: POLICY_REGISTRY_KEYS.root })
  }
  const fingerprint = () => definitionFingerprint(view.value)

  const contentLibrary = shallowRef<PaapiBaseline | null>(null)
  /* Re-read per stage so a publish, which writes Content Library, refreshes
     the baseline; the agent also prefills empty scoping rows on each read. */
  watch(
    () =>
      collab.status.value === 'open' && view.value.meta.id
        ? view.value.meta.stage
        : null,
    async (key) => {
      if (!key) return
      contentLibrary.value = await agent
        .call('baseline', [])
        .catch((error: unknown) => ({
          answers: null,
          error: errorMessage(error),
        }))
    },
    { immediate: true },
  )
  const contentLibraryChanges = computed(() =>
    contentLibrary.value?.answers
      ? paapiChanges(view.value.answers, contentLibrary.value.answers)
      : [],
  )

  const setFieldCitations: PolicyRoom['setFieldCitations'] = (
    fieldId,
    citations,
  ) =>
    commit(() =>
      replaceFieldCitations(
        doc.getMovableList(POLICY_DOC.citations),
        fieldId,
        citations,
      ),
    )

  return {
    collab,
    presence,
    peers,
    summary,
    notFound,
    view,
    title: computed(
      () =>
        policyTitle(view.value) || summary.value?.title || toValue(policyId),
    ),
    contentLibrary,
    contentLibraryChanges,
    stage,
    editable,
    canDiscuss: computed(
      () => canWrite.value && collab.status.value === 'open',
    ),

    setAnswer: (fieldId, value) =>
      commit(() => doc.getMap(POLICY_DOC.answers).set(fieldId, value)),
    addCitation: (citation) =>
      commit(() =>
        doc.getMovableList(POLICY_DOC.citations).push({
          ...citation,
          id: crypto.randomUUID(),
          by: user.value?.email ?? '',
          at: new Date().toISOString(),
        } satisfies Citation),
      ),
    setFieldCitations,
    markNotAsked: (fieldId, reason) =>
      commit(() => doc.getMap(POLICY_DOC.notAsked).set(fieldId, { reason })),
    pullIn: (fieldId) =>
      commit(() => doc.getMap(POLICY_DOC.notAsked).delete(fieldId)),
    addSource: (source) => upsert(POLICY_DOC.sources, source),
    removeSource: (id) => removeEntry(POLICY_DOC.sources, id),
    setDesign: (patch) =>
      commit(() =>
        applyDesignPatch(
          Object.fromEntries(
            DESIGN_LISTS.map((name) => [
              name,
              doc.getMovableList(POLICY_DOC[name]),
            ]),
          ) as unknown as Record<DesignListName, EntryList>,
          doc.getMap(POLICY_DOC.designMeta),
          patch,
        ),
      ),
    setCodeList: (id, list) =>
      commit(() => {
        const codeLists = doc.getMap(POLICY_DOC.codeLists)
        if (list) codeLists.set(id, list)
        else codeLists.delete(id)
      }),
    editTree: (change, placed) =>
      commit(() => {
        const before = {
          nodes: view.value.nodes,
          insights: view.value.insights,
        }
        const next = change(before)
        applyTreeEdit(
          {
            nodes: doc.getMovableList(POLICY_DOC.nodes),
            insights: doc.getMovableList(POLICY_DOC.insights),
            layout: doc.getMap(POLICY_DOC.layout),
          },
          before,
          next,
          view.value.layout,
          placed,
        )
      }),
    moveNodes: (direction, moves) =>
      commit(() => writePositions(doc.getMap(POLICY_DOC.layout), direction, moves)),
    tidyLayout: (direction) =>
      commit(() =>
        clearDirection(
          doc.getMap(POLICY_DOC.layout),
          Object.keys(view.value.layout),
          direction,
        ),
      ),
    linkNode: (nodeId, links) =>
      commit(() => doc.getMap(POLICY_DOC.nodeLinks).set(nodeId, links)),
    setDescription: (next) =>
      commit(() =>
        applyTextDiff(collab.document.text.value, view.value.description, next),
      ),

    ask: async (fieldId, text, opts) => {
      await send({ type: 'ask', fieldId, text, to: opts?.to, mentions: opts?.mentions })
    },
    answer: async (id, text, basis) => {
      await send({ type: 'answer', id, text, basis })
    },
    closeQuestion: async (id) => {
      await send({ type: 'close-question', id })
    },
    reassign: async (role, owner) => {
      await send({ type: 'reassign', role, owner })
    },
    approve: async (role, opts = {}) => {
      await send({
        type: 'approve',
        role,
        expectedFingerprint: fingerprint(),
        note: opts.note,
        evidenceUrl: opts.evidenceUrl,
      })
    },
    challenge: async (to, explanation, evidenceUrl) => {
      await send({
        type: 'challenge',
        to,
        expectedFingerprint: fingerprint(),
        explanation,
        evidenceUrl,
      })
    },
    moveBack: async (explanation, to) => {
      if (to === 'published' || to === 'dismissed')
        throw new Error(`Cannot move back to ${to}`)
      await send({ type: 'move-back', explanation, to })
    },
    respond: async (explanation) => {
      await send({ type: 'respond', explanation })
    },
    approveRecord: async (record) => {
      await send({
        type: 'approve-record',
        expectedFingerprint: fingerprint(),
        record,
      })
    },
    publishPolicy: async () => {
      await send({ type: 'publish' })
    },
    withdraw: async (note) => {
      await send({ type: 'withdraw', note })
    },
    attestClaims: async (evidenceUrl) => {
      await send({
        type: 'attest-claims',
        expectedFingerprint: fingerprint(),
        evidenceUrl,
      })
    },
    publishNow: async (record, evidenceUrl) => {
      await send({
        type: 'publish-now',
        expectedFingerprint: fingerprint(),
        record,
        evidenceUrl,
      })
    },
    reopen: async (note, to) => {
      await send({ type: 'reopen', note, to })
    },
    steerNode: async (nodeId, feedback) => {
      await send({ type: 'steer-node', nodeId: nodeId ?? undefined, feedback })
    },
    syncJdm: async () => {
      await send({ type: 'sync-jdm' })
    },
    generateClaims: async () => {
      await send({ type: 'generate-claims' })
    },
    judgeClaim: async (name, verdict, cause, note, question) => {
      await send({ type: 'judge-claim', name, verdict, cause, note, question })
    },
    markUnreachable: async (pathKey, reason) => {
      await send({ type: 'mark-unreachable', pathKey, reason })
    },
    claimSet: async () => agent.call('claimSet', []),
    approvedJdm: async () => agent.call('approvedJdm', []),
    blockers: {
      approve: (role) => approveBlockers(view.value, view.value.decisions, role),
      challenge: (input) =>
        challengeBlockers(view.value, view.value.decisions, input),
      moveBack: (explanation, to) => moveBackBlockers(view.value, explanation, to),
      respond: (text) => respondBlockers(view.value, view.value.decisions, text),
      record: (record) => recordBlockers(view.value, view.value.decisions, record),
      publish: () => publishBlockers(view.value, view.value.decisions),
      publishNow: (record, evidenceUrl) =>
        publishNowBlockers(view.value, view.value.decisions, record, evidenceUrl),
    },

    activity: async (query = {}) => agent.call('activity', [query]),

    comments: collab.comments.list,
    comment: (itemId, body, mentions, parentId) =>
      collab.comments.add(
        parentId
          ? { parentId, body, mentions }
          : { target: fieldCommentTarget(itemId), body, mentions },
      ),
    resolveThread: (id) => collab.comments.resolve(id),
    reopenThread: (id) => collab.comments.reopen(id),
    acceptSuggestion: async (id) => {
      await send({ type: 'accept-suggestion', id })
    },
    confirmProposal: async (fieldId) => {
      await send({ type: 'confirm-proposal', fieldId })
    },
    declineSuggestion: async (id, feedback) => {
      await agent.call('command', [
        { type: 'decline-suggestion', id, feedback },
      ])
    },
    undoField: async (fieldId) => {
      await send({ type: 'undo-field', fieldId })
    },
    redoField: async (fieldId) => {
      await send({ type: 'redo-field', fieldId })
    },
    restoreField: async (fieldId, versionId) => {
      await send({ type: 'restore-field', fieldId, versionId })
    },
    fieldHistory: async (fieldId, cursor) =>
      agent.call('fieldHistory', [{ fieldId, cursor }]),
    treeVersions: async () => agent.call('treeVersions', []),
    viewAt: (frontiers) => readPolicyDoc(doc.forkAt(frontiers)),
    versionSnapshot: async (seq) => agent.call('versionSnapshot', [seq]),
    saveTreeVersion: async (name, description) => {
      await agent.call('command', [
        { type: 'save-tree-version', name, description },
      ])
    },
    restoreTreeVersion: async (versionId) => {
      await send({ type: 'restore-tree-version', versionId })
    },
    addNote: async (itemId, body) => {
      await agent.call('command', [{ type: 'add-note', fieldId: itemId, body }])
    },
    editNote: async (id, body) => {
      await agent.call('command', [{ type: 'edit-note', id, body }])
    },
    removeNote: async (id) => {
      await agent.call('command', [{ type: 'remove-note', id }])
    },
    noteFromThread: async (root) => {
      const itemId = fieldIdFromCommentTarget(root.target)
      if (!itemId || activityIdFromItem(itemId))
        throw new Error(
          'Only a comment on the policy, a row or a node can become a note',
        )
      await agent.call('command', [
        { type: 'add-note', fieldId: itemId, body: root.body },
      ])
      collab.comments.resolve(root.id)
    },
    decisionFromThread: (root) => {
      const fieldId = fieldIdFromCommentTarget(root.target)
      if (!fieldId || !contractField(fieldId) || !editable.value) return false
      setFieldCitations(fieldId, [
        ...view.value.citations.filter((c) => c.fieldId === fieldId),
        decisionCitation(
          fieldId,
          root.body,
          user.value?.email ?? '',
          new Date().toISOString(),
          crypto.randomUUID(),
        ),
      ])
      return collab.comments.resolve(root.id)
    },
  }
}
